import { FC, useState } from 'react'

import { Button, Box, Typography, TextField } from '@mui/material'
import { FFModal } from '~/shared/ui'
import Autocomplete from '@mui/material/Autocomplete'
import { PhiAccessControlCreateBodyParams } from '@fireflyhealth/api'

type AccessReason = PhiAccessControlCreateBodyParams['access_reason']

const ACCESS_REASONS: Partial<Record<AccessReason, string>> = {
  other: 'Other',
  clinical_care: 'Clinical Care',
  quality: 'Quality',
  billing: 'Billing',
  tech: 'Tech Research / Support',
  operations: 'Operations Support',
  message_triage: 'Message Triage',
}

interface AccessReasonChoice {
  label: string
  value: AccessReason | null
  disabled?: boolean
}

const reasonValueAsOption = (reasonValue: AccessReason): AccessReasonChoice => {
  const label = ACCESS_REASONS[reasonValue]
  if (!label) {
    throw new Error(`Label not found for access reason: ${reasonValue}`)
  }
  return { label, value: reasonValue }
}

const ACCESS_REASON_CHOICES: AccessReasonChoice[] = (
  Object.keys(ACCESS_REASONS) as AccessReason[]
).map(reasonValueAsOption)

interface Props {
  cancel: () => void
  request: (reason: AccessReason) => void
  loading: boolean
}

const PHIAccessFormModal: FC<Props> = props => {
  const [accessReason, setAccessReason] = useState<AccessReason | null>(null)

  const currentlySelectedReason: AccessReasonChoice = accessReason
    ? reasonValueAsOption(accessReason)
    : { label: '', disabled: true, value: null }

  return (
    <FFModal
      open={true}
      header={
        <Box sx={{ p: 1 }}>
          <Typography variant="h6" align="center">
            This chart is confidential
          </Typography>
        </Box>
      }
      footer={
        <Box sx={{ my: 1, mx: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button color="secondary" disabled={props.loading} onClick={() => props.cancel()}>
            Cancel
          </Button>
          <Box sx={{ ml: 1 }}>
            <Button
              color="primary"
              variant="contained"
              disabled={!accessReason || props.loading}
              onClick={() => accessReason && props.request(accessReason)}
            >
              Request Access
            </Button>
          </Box>
        </Box>
      }
    >
      <Box sx={{ m: 2 }}>
        <Typography>
          This patient is marked as a VIP. Please help us to keep track of access to their Protected
          Health Information.
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Autocomplete
            autoHighlight
            getOptionLabel={option => option.label}
            value={currentlySelectedReason}
            getOptionDisabled={option => !!option.disabled}
            onChange={(_, option) => {
              if (option) {
                setAccessReason(option.value)
              }
            }}
            options={ACCESS_REASON_CHOICES}
            renderInput={params => (
              <TextField {...params} label="Choose a reason for access" variant="outlined" />
            )}
            style={{ width: 500 }}
          />
        </Box>
      </Box>
    </FFModal>
  )
}

export default PHIAccessFormModal
